import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import type { DirectorRisk } from "@/api/types";

/**
 * S5 Block 3 — Top risks (money/ops only, severity-sorted). source-badge per row
 * (nature of the risk); «Переглянути» = link/ghost drilldown (NO write-CTA, §0 /
 * D2=C read-only). Empty-state is neutral (no celebration). Data-quality (no-owner)
 * lives in a SEPARATE section, not here.
 */
function money(v: number | null): string | null {
  if (v === null) return null;
  if (Math.abs(v) >= 1_000_000) return `₴ ${(v / 1_000_000).toFixed(2)}М`;
  if (Math.abs(v) >= 1_000) return `₴ ${Math.round(v / 1_000)}К`;
  return `₴ ${Math.round(v)}`;
}

/** Resolve the backend advisory clickTarget to a real SPA route (§0 no dead-CTA). */
function resolveRoute(t: string): string {
  const [kind, qs] = t.split("?");
  const params = new URLSearchParams(qs ?? "");
  if (kind === "client") return `/clients/${params.get("holdingId") ?? ""}`;
  if (kind === "complaint") return `/complaints?id=${params.get("id") ?? ""}`;
  if (kind === "contract") return `/contracts?id=${params.get("id") ?? ""}`;
  return "/clients";
}

export function DirectorTopRisks({ risks }: { risks: DirectorRisk[] }) {
  const navigate = useNavigate();

  if (risks.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-center">
        <div className="text-[13.5px] font-semibold">Критичних ризиків немає</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Зараз немає org-wide ризиків, що потребують уваги директора.
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      {risks.map((r, i) => (
        <div key={`${r.type}-${i}`} className="flex min-h-16 items-center gap-3 border-b border-border px-4 py-3 last:border-0">
          <span
            className={cn(
              "h-2 w-2 flex-none rounded-full",
              r.severity <= 1 ? "bg-[hsl(var(--destructive))]" : r.severity === 2 ? "bg-[hsl(var(--warning-foreground))]" : "bg-[hsl(var(--info-foreground))]",
            )}
          />
          <span className="flex-none rounded-md bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {r.sourceBadge}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-semibold">{r.title}</div>
            <div className="truncate text-xs text-muted-foreground">
              клієнт <b className="font-semibold text-foreground">{r.clientName || "—"}</b>
            </div>
          </div>
          {money(r.amount) && <div className="flex-none text-[12.5px] font-bold tabular-nums text-[hsl(var(--destructive))]">{money(r.amount)}</div>}
          <button
            type="button"
            onClick={() => navigate(resolveRoute(r.clickTarget))}
            className="flex-none text-[12.5px] font-semibold text-[hsl(var(--primary))] hover:underline"
          >
            Переглянути →
          </button>
        </div>
      ))}
    </div>
  );
}
